import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useBranding, brandingFallback } from "@/lib/branding";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell,
} from "recharts";
import {
    BookOpen, Users, Inbox, Award, TrendingUp, Plus, Sparkles,
    ArrowRight, Play, Radio,
} from "lucide-react";
import CreateProgramWizard from "@/components/wizard/CreateProgramWizard";
import VideoRenderJobsStrip from "@/components/curriculum/VideoRenderJobsStrip";

const CONTAINER = {
    hidden: {},
    show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};
const ITEM = {
    hidden: { opacity: 0, y: 16 },
    show: {
        opacity: 1,
        y: 0,
        transition: { type: "spring", stiffness: 320, damping: 30 },
    },
};

function fmtShortDate(iso) {
    if (!iso) return "";
    const dt = new Date(`${iso}T00:00:00Z`);
    return `${dt.getUTCMonth() + 1}/${dt.getUTCDate()}`;
}

function SkeletonCard({ className = "" }) {
    return (
        <div
            className={`rounded-2xl bg-muted/40 border border-primary/5 animate-pulse ${className}`}
        />
    );
}

/* --- Welcome hero ------------------------------------------------------- */
function InstructorHero({ stats, onOpenWizard }) {
    const { user } = useAuth();
    const branding = useBranding();
    const firstName = (user?.name || "").split(" ")[0] || "there";
    return (
        <motion.section
            variants={ITEM}
            className="relative overflow-hidden rounded-2xl bg-card border border-primary/10 p-8 sm:p-12"
            data-testid="instructor-welcome-hero"
        >
            <img
                src={branding.logo_url}
                alt=""
                aria-hidden
                className="pointer-events-none absolute -right-10 -bottom-12 h-64 w-64 object-contain opacity-[0.05] select-none" onError={brandingFallback}
            />
            <div
                aria-hidden
                className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-accent/12 blur-3xl"
            />
            <div className="relative z-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                <div>
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-accent font-semibold">
                        <Radio className="h-3.5 w-3.5" />
                        Instructor Studio
                    </div>
                    <h1
                        className="font-display text-4xl sm:text-5xl lg:text-6xl text-primary tracking-tight mt-3 leading-[1.02]"
                        data-testid="instructor-hero-title"
                    >
                        Hello, <span className="text-accent">{firstName.replace(/\.$/, "")}</span>.
                    </h1>
                    <p className="mt-4 text-base text-muted-foreground max-w-xl leading-relaxed">
                        {stats?.student_count
                            ? `${stats.student_count} student${stats.student_count === 1 ? "" : "s"} enrolled across ${stats.program_count} program${stats.program_count === 1 ? "" : "s"}. ${stats.active_this_week || 0} studied this week.`
                            : "Build programs, publish lessons, and grade student work — all from one place."}
                    </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
                    <Button
                        onClick={onOpenWizard}
                        className="bg-accent hover:bg-accent/90 text-accent-foreground gap-2"
                        data-testid="instructor-new-program-btn"
                    >
                        <Plus className="h-4 w-4" />
                        New program
                    </Button>
                    <Button
                        asChild
                        variant="outline"
                        className="border-primary/20 hover:bg-primary/5"
                        data-testid="instructor-inbox-button"
                    >
                        <Link to="/instructor/inbox" className="gap-2">
                            <Inbox className="h-4 w-4" />
                            Inbox
                            {stats?.unread_inbox > 0 && (
                                <Badge className="bg-accent text-accent-foreground h-5 min-w-[20px] px-1.5 rounded-full">
                                    {stats.unread_inbox}
                                </Badge>
                            )}
                        </Link>
                    </Button>
                </div>
            </div>
        </motion.section>
    );
}

/* --- Stat tile ---------------------------------------------------------- */
function StatTile({ label, value, icon: Icon, tone = "primary", testid, hint }) {
    const toneBg = tone === "accent" ? "bg-accent/10" : "bg-primary/5";
    const toneRing = tone === "accent" ? "ring-accent/20" : "ring-primary/10";
    return (
        <motion.div
            variants={ITEM}
            whileHover={{ y: -3 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="group relative rounded-2xl bg-card border border-primary/10 p-6 overflow-hidden"
            data-testid={testid}
        >
            <div
                aria-hidden
                className="absolute -top-10 -right-10 h-24 w-24 rounded-full bg-accent/[0.06] blur-xl"
            />
            <div className="relative flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">
                        {label}
                    </div>
                    <div className="font-display text-4xl text-primary mt-3 leading-none">
                        {value}
                    </div>
                    {hint && (
                        <div className="mt-2 text-xs text-muted-foreground">
                            {hint}
                        </div>
                    )}
                </div>
                <div className={`h-10 w-10 rounded-xl ${toneBg} ring-1 ${toneRing} flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`h-4.5 w-4.5 ${tone === "accent" ? "text-accent" : "text-primary"}`} />
                </div>
            </div>
        </motion.div>
    );
}

/* --- Weekly engagement chart ------------------------------------------- */
function WeeklyEngagement({ series }) {
    const data = (series || []).map((d) => ({
        day: fmtShortDate(d.day),
        minutes: d.minutes || 0,
    }));
    const total = data.reduce((s, d) => s + d.minutes, 0);
    const peak = data.reduce((m, d) => Math.max(m, d.minutes), 0);
    return (
        <motion.div
            variants={ITEM}
            className="h-full rounded-2xl bg-card border border-primary/10 p-6"
            data-testid="instructor-weekly-engagement"
        >
            <div className="flex items-center justify-between mb-3">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.22em] text-accent font-semibold">
                        Weekly engagement
                    </div>
                    <h2 className="font-display text-xl text-primary tracking-tight mt-1">
                        Study minutes · last 7 days
                    </h2>
                </div>
                <TrendingUp className="h-5 w-5 text-accent/60" />
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="rounded-lg bg-primary/5 p-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                        Total
                    </div>
                    <div className="font-display text-xl text-primary mt-0.5 leading-none">
                        {total}
                        <span className="text-[10px] text-muted-foreground ml-1 font-sans">min</span>
                    </div>
                </div>
                <div className="rounded-lg bg-accent/10 p-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                        Peak day
                    </div>
                    <div className="font-display text-xl text-primary mt-0.5 leading-none">
                        {peak}
                        <span className="text-[10px] text-muted-foreground ml-1 font-sans">min</span>
                    </div>
                </div>
            </div>
            <div className="h-40 -mx-2">
                {data.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-xs text-muted-foreground">
                        No data yet
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data}>
                            <XAxis
                                dataKey="day"
                                tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <YAxis hide />
                            <Tooltip
                                cursor={{ fill: "hsl(var(--muted) / 0.4)" }}
                                contentStyle={{
                                    background: "hsl(var(--card))",
                                    border: "1px solid hsl(var(--border))",
                                    borderRadius: 6,
                                    fontSize: 11,
                                }}
                                formatter={(v) => [`${v} min`, "Studied"]}
                            />
                            <Bar dataKey="minutes" radius={[4, 4, 0, 0]}>
                                {data.map((d, i) => (
                                    <Cell
                                        key={i}
                                        fill={d.minutes === peak && peak > 0
                                            ? "#D4A140"
                                            : "hsl(var(--primary))"}
                                    />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </div>
        </motion.div>
    );
}

/* --- Program row in the instructor's program list ---------------------- */
function ProgramRollupRow({ p }) {
    return (
        <motion.div
            variants={ITEM}
            whileHover={{ y: -2 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="group rounded-xl bg-card border border-primary/10 p-5 hover:border-primary/20 hover:shadow-lg hover:shadow-primary/5 transition-all"
            data-testid={`instructor-program-row-${p.id}`}
        >
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-2">
                        {p.subject && (
                            <Badge
                                variant="outline"
                                className="capitalize text-[10px] font-normal"
                            >
                                {p.subject}
                            </Badge>
                        )}
                        {p.level && (
                            <Badge
                                variant="outline"
                                className="capitalize text-[10px] font-normal"
                            >
                                {p.level}
                            </Badge>
                        )}
                        {!p.published && (
                            <Badge
                                variant="outline"
                                className="text-[10px] font-normal bg-muted/60"
                            >
                                Draft
                            </Badge>
                        )}
                        {p.archived && (
                            <Badge
                                variant="outline"
                                className="text-[10px] font-normal bg-muted/60"
                            >
                                Archived
                            </Badge>
                        )}
                    </div>
                    <h3 className="font-display text-lg text-primary leading-tight">
                        {p.title}
                    </h3>
                    <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1">
                            <Users className="h-3.5 w-3.5" />
                            {p.students} student{p.students === 1 ? "" : "s"}
                        </span>
                        <span className="inline-flex items-center gap-1">
                            <BookOpen className="h-3.5 w-3.5" />
                            {p.chapter_count} chapter{p.chapter_count === 1 ? "" : "s"}
                        </span>
                    </div>
                    <div className="mt-3">
                        <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1">
                            <span>Average progress</span>
                            <span className="font-medium text-primary tabular-nums">
                                {p.avg_progress}%
                            </span>
                        </div>
                        <div className="h-1.5 rounded-full bg-primary/5 overflow-hidden">
                            <div
                                className="h-full rounded-full shimmer-bar"
                                style={{
                                    width: `${p.avg_progress}%`,
                                    background:
                                        "linear-gradient(90deg, #D4A140 0%, #E8C07A 50%, #D4A140 100%)",
                                    backgroundSize: "200% 100%",
                                }}
                            />
                        </div>
                    </div>
                </div>
                <div className="flex-shrink-0 flex flex-col gap-2">
                    <Button
                        asChild
                        size="sm"
                        className="bg-primary hover:bg-primary/90 gap-1.5"
                        data-testid={`instructor-program-manage-${p.id}`}
                    >
                        <Link to={`/instructor/programs/${p.id}`}>
                            <Play className="h-3.5 w-3.5" />
                            Manage
                        </Link>
                    </Button>
                    <Button
                        asChild
                        size="sm"
                        variant="outline"
                        className="gap-1.5 border-primary/20"
                        data-testid={`instructor-program-students-${p.id}`}
                    >
                        <Link to={`/instructor/programs/${p.id}?tab=students`}>
                            <Users className="h-3.5 w-3.5" />
                            Students
                        </Link>
                    </Button>
                </div>
            </div>
        </motion.div>
    );
}

/* --- Page --------------------------------------------------------------- */
export default function InstructorDashboard() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [wizardOpen, setWizardOpen] = useState(false);

    const loadStats = React.useCallback(() => {
        setLoading(true);
        api.get("/instructor/dashboard-stats")
            .then((r) => setStats(r.data))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        loadStats();
    }, [loadStats]);

    const activePrograms = useMemo(
        () => (stats?.programs || []).filter((p) => !p.archived),
        [stats],
    );

    return (
        <motion.div
            variants={CONTAINER}
            initial="hidden"
            animate="show"
            className="max-w-7xl mx-auto px-6 lg:px-8 py-10 lg:py-14 space-y-8"
            data-testid="instructor-dashboard"
        >
            <InstructorHero stats={stats} onOpenWizard={() => setWizardOpen(true)} />

            {/* Background video-render progress (only renders when the
                current user has in-flight or just-finished jobs). */}
            <VideoRenderJobsStrip />

            {/* Bento: weekly chart + 3 stats */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                <div className="lg:col-span-8">
                    {loading ? (
                        <SkeletonCard className="h-full min-h-[300px]" />
                    ) : (
                        <WeeklyEngagement series={stats?.weekly_minutes} />
                    )}
                </div>
                <div className="lg:col-span-4 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 gap-4">
                    {loading ? (
                        <>
                            <SkeletonCard className="h-28" />
                            <SkeletonCard className="h-28" />
                            <SkeletonCard className="h-28" />
                        </>
                    ) : (
                        <>
                            <StatTile
                                label="Total students"
                                value={stats?.student_count || 0}
                                icon={Users}
                                testid="instructor-stat-students"
                                hint={
                                    stats?.active_this_week
                                        ? `${stats.active_this_week} active this week`
                                        : "No active students this week"
                                }
                            />
                            <StatTile
                                label="Unread inbox"
                                value={stats?.unread_inbox || 0}
                                icon={Inbox}
                                tone={stats?.unread_inbox ? "accent" : "primary"}
                                testid="instructor-stat-inbox"
                                hint={
                                    stats?.unread_inbox
                                        ? "Student questions await"
                                        : "Inbox clear"
                                }
                            />
                            <StatTile
                                label="Certificates issued"
                                value={stats?.certs_issued || 0}
                                icon={Award}
                                testid="instructor-stat-certs"
                                hint="Students who completed a program"
                            />
                        </>
                    )}
                </div>
            </div>

            {/* Programs */}
            <motion.section variants={ITEM} data-testid="instructor-programs-section">
                <div className="flex items-end justify-between mb-6">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.22em] text-accent font-semibold">
                            Your classroom
                        </div>
                        <h2 className="font-display text-3xl text-primary mt-1 tracking-tight">
                            My programs
                        </h2>
                    </div>
                    {stats?.program_count > 0 && (
                        <div className="text-sm text-muted-foreground">
                            {activePrograms.length} active · {stats.program_count} total
                        </div>
                    )}
                </div>

                {loading ? (
                    <div className="space-y-3">
                        <SkeletonCard className="h-36" />
                        <SkeletonCard className="h-36" />
                    </div>
                ) : activePrograms.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-primary/15 p-12 text-center bg-card/40">
                        <Sparkles className="h-10 w-10 text-accent/60 mx-auto mb-3" />
                        <div className="font-display text-xl text-primary mb-1">
                            No programs yet
                        </div>
                        <div className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
                            Start your first program — add chapters, lessons,
                            quizzes, and publish it when you're ready.
                        </div>
                        <Button
                            onClick={() => setWizardOpen(true)}
                            className="bg-accent hover:bg-accent/90 text-accent-foreground gap-2"
                            data-testid="instructor-empty-new-program-btn"
                        >
                            <Plus className="h-4 w-4" />
                            Create your first program
                        </Button>
                    </div>
                ) : (
                    <motion.div variants={CONTAINER} className="space-y-3">
                        {activePrograms.map((p) => (
                            <ProgramRollupRow key={p.id} p={p} />
                        ))}
                    </motion.div>
                )}
            </motion.section>

            <CreateProgramWizard
                open={wizardOpen}
                onOpenChange={setWizardOpen}
                apiPath="/instructor/programs"
                hideInstructorPicker
                onCreated={loadStats}
            />
        </motion.div>
    );
}
